/**
 * StepSummary.jsx
 *
 * Wizard step 6 — read-back of every selection plus "View Results" CTA.
 *
 * PORTED from TH ToolFinder v4:
 *   tophammer-toolfinder-v4/src/components/wizard/StepSummary.jsx (67 lines).
 *
 * Structure:
 *   - Header: "Your selections" / "Review your choices before viewing results".
 *   - Rig context (if selections.rigModel set — Replace path B).
 *   - Rock drill context (if selections.rockDrillModel set — Replace path C).
 *   - SelectionSummary prose line.
 *   - Back / View Results buttons.
 *
 * MRT divergences from TF v4:
 *   - Reads selections + handlers from useWizard() (no prop drilling).
 *   - "View Results" calls handleViewResults — that handler sets
 *     currentStep to RESULTS which fires onReachResults → router navigates
 *     to /toolfinder/results (already wired in ToolFinderPage).
 *   - Brand tokens (electric-blue, eb-3, n1, n5) instead of TF v4 raw hex.
 *   - No rounded corners on buttons / context cards (Sandvik brand rule).
 *
 * MRT addition — Edit pencil affordances: SKIPPED for Sub-Plan 2 per
 *   plan §3.D3f ("if it adds too much complexity, skip it and we'll add
 *   in a polish pass"). The plumbing is here in the brief; once a final
 *   icon component lands and a per-section row design is approved we can
 *   reintroduce. Setting `setCurrentStep(STEPS.X)` is already available
 *   from useWizard if a follow-up wants to wire it.
 *
 * @module components/wizard/StepSummary
 * @since 2026-05-17 (Group D Sub-Plan 2, Task D3f)
 */

import { useWizard } from '@contexts/WizardContext';
import SelectionSummary from '@components/ui/SelectionSummary';

export default function StepSummary() {
  const { selections, handleSummaryPrevious, handleViewResults } = useWizard();

  return (
    <section
      aria-labelledby="summary-heading"
      className="max-w-2xl mx-auto"
    >
      <header className="mb-8 text-center">
        <h1
          id="summary-heading"
          className="font-sandvik-headline text-2xl font-normal text-carbon-black mb-2"
        >
          Your selections
        </h1>
        <p className="font-sandvik-text text-n6">
          Review your choices before viewing results
        </p>
      </header>

      {/* Rig context (Replace path B) */}
      {selections.rigModel && (
        <div
          data-testid="rig-context"
          className="bg-n1 p-4 mb-6"
        >
          <p className="font-sandvik-text text-xs text-n5 tracking-wider mb-1">
            Selected rig
          </p>
          <p className="font-sandvik-headline font-normal text-lg text-carbon-black">
            {selections.rigModel.productName}
          </p>
          <p className="font-sandvik-text text-sm text-n6">
            Rock Drill: {selections.rigModel.rockDrill?.primary}
            {selections.rigModel.rockDrill?.primaryPowerKw &&
              ` (${selections.rigModel.rockDrill.primaryPowerKw} kW)`}
          </p>
        </div>
      )}

      {/* Rock Drill context (Replace path C) */}
      {selections.rockDrillModel && (
        <div
          data-testid="drill-context"
          className="bg-n1 p-4 mb-6"
        >
          <p className="font-sandvik-text text-xs text-n5 tracking-wider mb-1">
            Selected rock drill
          </p>
          <p className="font-sandvik-headline font-normal text-lg text-carbon-black">
            {selections.rockDrillModel.model}
          </p>
          <p className="font-sandvik-text text-sm text-n6">
            {selections.rockDrillModel.powerKw} kW &middot; {selections.rockDrillModel.family} Series
          </p>
        </div>
      )}

      <SelectionSummary selections={selections} className="mb-8" />

      <div className="flex justify-center gap-4">
        <button
          type="button"
          onClick={handleSummaryPrevious}
          className="px-6 py-3 border border-n3 text-carbon-black hover:border-n5 transition-colors font-sandvik-text font-normal"
        >
          &larr; Back
        </button>
        <button
          type="button"
          data-testid="view-results-cta"
          onClick={handleViewResults}
          className="px-8 py-3 bg-electric-blue text-white hover:bg-eb-3 transition-colors font-sandvik-text font-normal inline-flex items-center gap-2"
        >
          View Results
          <span aria-hidden="true">&rarr;</span>
        </button>
      </div>
    </section>
  );
}
